import { z } from 'zod';
import {
  paymentMilestoneSchema,
  paymentScheduleItemTypeSchema,
  paymentTriggerTypeSchema,
} from './payment-schedule.schema';

/**
 * Gespeicherte Zahlungsplan-Vorlage (ADR 0077, Nachtrag 2026-07-31). Ersetzt die
 * frühere hartcodierte Konstante `PAYMENT_SCHEDULE_TEMPLATES`, die nur Prozentsätze
 * kannte — die Bezeichnung jeder Zeile wurde aus deren Position geraten
 * („Anzahlung", „Teilzahlung 1", „Schlussrechnung"). Seit die Bezeichnung als
 * Überschrift im Rechnungs-PDF landet (`SalesDocument.titleOverride`), muss sie
 * aus der Vorlage kommen und nicht aus einer Heuristik.
 *
 * Eine Vorlage trägt deshalb je Zeile ALLES, was eine geplante Position ausmacht —
 * außer dem Geld: siehe `basis`-Hinweis unten.
 */

/**
 * Eine Zeile einer Vorlage.
 *
 * **Bewusst nur Prozent, kein Festbetrag** (`basis: 'AMOUNT'` gibt es hier nicht):
 * Eine Vorlage ist ein Muster („30 / 70"), das auf Aufträge unterschiedlicher Höhe
 * passen muss. Ein fester Euro-Betrag ist auftragsspezifisch und in einer
 * tenant-weiten Vorlage sinnlos. Nebeneffekt: Es landet kein `Money`/`bigint` im
 * JSONB (CLAUDE.md §6 — `bigint` hat keine JSON-Darstellung).
 *
 * **`triggerDate` fehlt ebenfalls bewusst.** Ein konkretes Datum gehört zum
 * Auftrag, nicht zum Muster. `triggerType: 'DATE'` ist erlaubt und heißt „Datum,
 * beim Anwenden auszufüllen" — die angewandte Zeile kommt ohne Datum, und die
 * bestehende Feldprüfung (`validateDraftRow`) markiert sie als unvollständig.
 */
export const paymentScheduleTemplateRowSchema = z
  .object({
    type: paymentScheduleItemTypeSchema,
    label: z.string().trim().min(1).max(200),
    triggerType: paymentTriggerTypeSchema,
    milestone: paymentMilestoneSchema.optional(),
    milestoneCustomText: z.string().trim().max(200).optional(),
    /** Anteil am Auftragswert. Zwei Nachkommastellen wie am Plan selbst. */
    percentage: z.number().gt(0).max(100),
  })
  .refine((row) => row.triggerType !== 'MILESTONE' || row.milestone != null, {
    message: 'Bei Auslöser „Meilenstein" ist ein Meilenstein erforderlich.',
    path: ['milestone'],
  })
  .refine((row) => row.milestone !== 'CUSTOM' || (row.milestoneCustomText ?? '').length > 0, {
    message: 'Bei Meilenstein „individuell" ist ein Text erforderlich.',
    path: ['milestoneCustomText'],
  });
export type PaymentScheduleTemplateRow = z.infer<typeof paymentScheduleTemplateRowSchema>;

/**
 * Summe aller Anteile in Basispunkten (1 % = 100 bp) — ganzzahlig, damit die
 * Prüfung „ergibt 100 %" nicht an Float-Rundung scheitert (0.1 + 0.2 ≠ 0.3).
 */
export function templatePercentageBasisPoints(rows: readonly { percentage: number }[]): number {
  return rows.reduce((sum, row) => sum + Math.round(row.percentage * 100), 0);
}

const TOTAL_BASIS_POINTS = 100 * 100;

/**
 * Die Zeilen einer Vorlage müssen zusammen 100 % ergeben. Eine Vorlage, die das
 * nicht tut, würde beim Anwenden sofort in die rote Summenmeldung am Plan laufen —
 * dann darf sie gar nicht erst entstehen.
 */
const rowsSchema = z
  .array(paymentScheduleTemplateRowSchema)
  .min(1, 'Eine Vorlage braucht mindestens eine Position.')
  .max(20, 'Eine Vorlage fasst höchstens 20 Positionen.')
  .refine((rows) => templatePercentageBasisPoints(rows) === TOTAL_BASIS_POINTS, {
    message: 'Die Anteile einer Vorlage müssen zusammen 100 % ergeben.',
  });

export const paymentScheduleTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  rows: z.array(paymentScheduleTemplateRowSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PaymentScheduleTemplateResponse = z.infer<typeof paymentScheduleTemplateResponseSchema>;

export const createPaymentScheduleTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  rows: rowsSchema,
});
export type CreatePaymentScheduleTemplateInput = z.infer<typeof createPaymentScheduleTemplateSchema>;

export const updatePaymentScheduleTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  rows: rowsSchema.optional(),
});
export type UpdatePaymentScheduleTemplateInput = z.infer<typeof updatePaymentScheduleTemplateSchema>;

/**
 * Kurzfassung der Anteile für den Vorlagen-Button („30 / 70"). Ohne Dezimalnullen,
 * damit aus 30 nicht „30,00" wird.
 */
export function templatePercentageSummary(rows: readonly { percentage: number }[]): string {
  return rows.map((row) => String(row.percentage).replace('.', ',')).join(' / ');
}
